import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Palette, Check, Target, ShieldCheck, FileBarChart, Rocket } from 'lucide-react';

const OpsPage = () => {

  const opsServices = [
    {
      title: 'Ad Operations',
      icon: <Target className="w-8 h-8" />,
      description: 'Your team handles the full campaign execution layer — set up, trafficked, optimized, and monitored under your brand.',
      features: [
        'Campaign setup & trafficking',
        'Bid optimization & budget pacing',
        'Daily performance monitoring',
        'Ad platform troubleshooting',
        'Quality assurance & pre-flight checks',
        'Conversion tracking setup & QA'
      ]
    },
    {
      title: 'Analytics & Reporting',
      icon: <BarChart3 className="w-8 h-8" />,
      description: 'White-label dashboards and presentation-ready insights your clients will think came from your own team.',
      features: [
        'Custom white-label reporting dashboards',
        'Multi-channel attribution modeling',
        'ETL & data pipeline management',
        'Performance analysis & insights',
        'Automated report generation',
        'Presentation-ready data storytelling'
      ]
    },
    {
      title: 'Creative Operations',
      icon: <Palette className="w-8 h-8" />,
      description: 'Production-ready creative ops that keep every format, feed, and test moving without bottlenecking your team. Note: we handle creative operations and adaptation — creative strategy and original production stay with your team or your client\'s creative partner.',
      features: [
        'Ad creative resizing & adaptation for all formats',
        'Dynamic product feed management',
        'Creative performance analysis & A/B testing coordination',
        'Quality assurance processes',
        'Asset organization & management'
      ]
    }
  ];

  const benefits = [
    {
      stat: '40–60%',
      title: 'Cost Savings',
      description: 'Compared to hiring in-house ops staff, with zero training time.'
    },
    {
      stat: '48 Hours',
      title: 'To Go Live',
      description: 'Your dedicated team is briefed, assigned, and operational within 2 business days.'
    },
    {
      stat: '5-Day',
      title: 'Onboarding',
      description: 'From signed agreement to a fully operational, embedded ops team.'
    },
    {
      stat: 'Zero',
      title: 'Lock-In',
      description: 'Rolling monthly contracts. Scale up, down, or cancel anytime.'
    }
  ];

  return (
    <div className="min-h-screen py-20" data-testid="ops-page">
      <div className="container">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="ops-hero-title">
            Your Agency's Dedicated Ad Ops Team — Without the Overhead
          </h1>
          <p className="body-lg mb-12">
            Growing agencies don't need to hire. They need a proven ops team that plugs in,
            runs campaigns, and delivers — under your brand, on your timeline. MarqiOps is the
            white-label ad operations partner built exclusively for agencies.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="ops-hero-cta-primary">
              Get Started Today — Live in 48 Hours
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/ops/pricing" className="btn-secondary" data-testid="ops-hero-cta-secondary">
              View Pricing Plans
            </Link>
          </div>
        </div>

        {/* Why Choose MarqiOps */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Run More Campaigns. Hire Nobody.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Your clients need results. Your team needs bandwidth. MarqiOps gives you a dedicated
              ad operations team that works inside your existing workflows — no recruiting, no
              onboarding delays, no payroll overhead.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className={`feature-card text-center hover-lift animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="h2 text-[var(--accent-primary)] mb-4">{benefit.stat}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{benefit.title}</h3>
                <p className="body-md">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Services Overview */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Every Part of Ad Ops, Handled.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              From campaign trafficking to creative QA, your MarqiOps team manages the full
              execution layer — so your team focuses on strategy and client relationships, not
              spreadsheets and platform troubleshooting.
            </p>
          </div>

          <div className="space-y-12">
            {opsServices.map((service, index) => (
              <div key={service.title} className={`feature-card animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="grid lg:grid-cols-3 gap-8 items-center">
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-2xl flex items-center justify-center mr-6">
                        <div className="text-[var(--accent-primary)]">{service.icon}</div>
                      </div>
                      <h3 className="h1 text-[var(--text-primary)]">{service.title}</h3>
                    </div>
                    <p className="body-lg">{service.description}</p>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="grid md:grid-cols-2 gap-4">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-center space-x-3 p-4 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)]">
                          <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0" />
                          <span className="text-[var(--text-secondary)]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section */}
        <section className="mb-24">
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Transparent Pricing. Built for Agencies.
            </h2>
            <p className="body-lg max-w-3xl mx-auto mb-8">
              No long-term contracts. No surprise fees. Scale your ops pod up or down as your client roster
              changes — every plan includes white-label delivery.
            </p>
            <div className="mb-8">
              <span className="text-[var(--text-muted)]">Plans start from</span>
              <div className="h1 text-[var(--accent-primary)] my-2">$1,500<span className="text-lg text-[var(--text-muted)]">/mo</span></div>
              <span className="text-[var(--text-muted)]">— or try a Trial Week for a flat $500.</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/ops/pricing" className="btn-primary" data-testid="ops-pricing-cta-view-plans">
                View All Plans & Pricing
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid="ops-pricing-cta-trial">
                Start a $500 Trial Week
              </Link>
            </div>
          </div>
        </section>

        {/* How MarqiOps Works */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl" data-testid="ops-how-it-works">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              How MarqiOps Works for Agencies Like Yours
            </h2>
            <p className="body-lg max-w-3xl mx-auto mb-12">
              We don't publish case studies until we've earned them. What we can show you is exactly how
              we work — our SLAs, our reporting format, and our onboarding process — so you know what to
              expect before you sign anything. Book a 20-minute call and we'll walk you through a live demo
              of our reporting dashboard.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12 text-left">
              <div className="feature-card">
                <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-xl flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-[var(--accent-primary)]" />
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-2">Clear SLAs</h3>
                <p className="body-md">Defined response times, uptime commitments, and delivery standards written into every engagement.</p>
              </div>
              <div className="feature-card">
                <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-xl flex items-center justify-center mb-4">
                  <FileBarChart className="w-6 h-6 text-[var(--accent-primary)]" />
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-2">White-Label Reporting</h3>
                <p className="body-md">See a live demo of the exact dashboard and monthly report your clients receive — under your brand.</p>
              </div>
              <div className="feature-card">
                <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-xl flex items-center justify-center mb-4">
                  <Rocket className="w-6 h-6 text-[var(--accent-primary)]" />
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-2">5-Day Onboarding</h3>
                <p className="body-md">From signed agreement to an embedded, operational ops team in five business days.</p>
              </div>
            </div>

            <Link to="/contact" className="btn-primary" data-testid="ops-how-it-works-cta">
              Book a 20-Minute Walkthrough
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Process Section */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Up and Running in 48 Hours.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              No lengthy onboarding. No agency jargon. Just a simple three-step process to get your
              dedicated MarqiOps team working inside your accounts, under your brand.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery Call',
                description: 'We assess your client portfolio, current processes, platforms, and volume. We identify gaps and assign the right specialists before we start.'
              },
              {
                step: '02',
                title: 'Team Assembly',
                description: 'We match you with certified specialists based on your client verticals and platform mix — Google, Meta, DV360, The Trade Desk, Amazon, and more.'
              },
              {
                step: '03',
                title: 'Launch & Scale',
                description: 'Your team goes live inside your client accounts, operating under your brand. You get full visibility, daily updates, and a team that scales with your wins.'
              }
            ].map((step, index) => (
              <div key={step.step} className={`text-center animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="w-20 h-20 bg-[var(--accent-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-[var(--accent-primary)]">{step.step}</span>
                </div>
                <h3 className="h2 text-[var(--text-primary)] mb-4">{step.title}</h3>
                <p className="body-md">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="feature-card">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Your Next Client Win Shouldn't Cost You a New Hire.
            </h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Stop losing pitches because your team is stretched. Stop turning down clients because
              you don't have the ops bandwidth. MarqiOps gives you the execution firepower to say
              yes — and deliver.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect" data-testid="ops-final-cta-primary">
                Start Your Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/ops/pricing" className="btn-secondary" data-testid="ops-final-cta-secondary">
                Compare Plans
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OpsPage;
